import type { ImportStatus, JobStatus } from '@servicetim/shared';
import {
  useCancelImport,
  useDiscoverImport,
  useDryRunImport,
  useImport,
  useImportReport,
  useJob,
  useRunImport,
} from '@servicetim/ui/api';
import {
  Button,
  LoadingState,
  Page,
  PageHeader,
  ProblemBanner,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
  toast,
} from '@servicetim/ui/components';
import { useEffect, useState } from 'react';
import { formatDateTime } from '../tickets/format';
import { ImportStatusBadge } from './import-status-badge';
import { MappingPanel } from './mapping/mapping-panel';
import { ReportTab } from './report-tab';
import { RunTab } from './run-tab';

const TERMINAL_JOB: ReadonlySet<JobStatus> = new Set(['done', 'failed']);
const CANCELLABLE: ReadonlySet<ImportStatus> = new Set([
  'discovering',
  'mapping',
  'dry_run',
  'running',
]);

/**
 * `/imports/$importId` (S-09-10): header with status + actions (Discover → Map → Dry run →
 * Run / Cancel / Re-run), then Mapping / Report / Run tabs. A triggered action's `jobId` is
 * polled via `useJob` (small imports already ran inline by the time the POST resolves — the
 * very first poll just sees it terminal); the batch itself is polled too while a job is in
 * flight, or while its own status is unambiguously active (`running`/`discovering` — unlike
 * `dry_run`, which is also a settled "blocking report" state, so it alone never implies polling).
 */
export function ImportDetailScreen({ importId }: { importId: string }) {
  const [activeJobId, setActiveJobId] = useState<string | null>(null);
  const [pollBatch, setPollBatch] = useState(false);
  const [completePartially, setCompletePartially] = useState(false);

  const batch = useImport(importId, { pollMs: pollBatch ? 3000 : false });
  const job = useJob(activeJobId ?? undefined, { surface: 'admin', pollMs: 3000 });
  const report = useImportReport(batch.data?.discovery ? importId : undefined);

  useEffect(() => {
    const status = batch.data?.status;
    setPollBatch(activeJobId !== null || status === 'running' || status === 'discovering');
  }, [activeJobId, batch.data?.status]);

  useEffect(() => {
    if (activeJobId && job.data && TERMINAL_JOB.has(job.data.status)) {
      setActiveJobId(null);
      void batch.refetch();
      void report.refetch();
      if (job.data.status === 'failed') {
        toast.error(job.data.error ?? 'The job failed');
      }
    }
  }, [activeJobId, job.data, batch.refetch, report.refetch]);

  const discover = useDiscoverImport(importId);
  const dryRun = useDryRunImport(importId);
  const run = useRunImport(importId);
  const cancel = useCancelImport(importId);

  if (batch.isPending) {
    return <LoadingState rows={6} label="Loading the import…" />;
  }
  if (batch.error || !batch.data) {
    return <ProblemBanner error={batch.error} onRetry={() => void batch.refetch()} />;
  }
  const b = batch.data;
  const busy = activeJobId !== null;

  const runAction = async (trigger: () => Promise<{ jobId: string }>, busyMessage: string) => {
    try {
      const result = await trigger();
      setActiveJobId(result.jobId);
      toast.success(busyMessage);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to start the job');
    }
  };

  return (
    <Page>
      <PageHeader
        title={`Import ${b.source}`}
        meta={<ImportStatusBadge status={b.status} />}
        description={`Created ${formatDateTime(b.createdAt)}${b.startedAt ? ` · started ${formatDateTime(b.startedAt)}` : ''}${b.finishedAt ? ` · finished ${formatDateTime(b.finishedAt)}` : ''}`}
        actions={
          <div className="flex gap-2">
            <Button
              variant="outline"
              disabled={busy}
              onClick={() => void runAction(() => discover.mutateAsync(), 'Discovery started')}
            >
              Discover
            </Button>
            <Button
              variant="outline"
              disabled={busy || !b.discovery}
              onClick={() => void runAction(() => dryRun.mutateAsync(), 'Dry run started')}
            >
              Dry run
            </Button>
            <Button
              disabled={busy || !b.discovery}
              onClick={() =>
                void runAction(
                  () => run.mutateAsync({ completePartially }),
                  b.status === 'completed' ? 'Re-run started' : 'Run started',
                )
              }
            >
              {b.status === 'completed' ? 'Re-run' : 'Run'}
            </Button>
            <Button
              variant="outline"
              disabled={!CANCELLABLE.has(b.status)}
              onClick={() =>
                void cancel
                  .mutateAsync()
                  .then(() => toast.success('Cancelled'))
                  .catch((err) => toast.error(err instanceof Error ? err.message : 'Cancel failed'))
              }
            >
              Cancel
            </Button>
          </div>
        }
      />

      {b.error ? <ProblemBanner error={b.error} title="The import failed" /> : null}

      <Tabs defaultValue="mapping">
        <TabsList aria-label="Import sections">
          <TabsTrigger value="mapping">Mapping</TabsTrigger>
          <TabsTrigger value="report">Report</TabsTrigger>
          <TabsTrigger value="run">Run</TabsTrigger>
        </TabsList>
        <TabsContent value="mapping">
          {b.discovery && b.projectId ? (
            <MappingPanel importId={importId} projectId={b.projectId} discovery={b.discovery} />
          ) : (
            <p className="text-fg-muted text-sm">Discover the source first to map its fields.</p>
          )}
        </TabsContent>
        <TabsContent value="report">
          <ReportTab
            report={report.data}
            isPending={report.isPending && b.discovery !== null}
            error={report.error}
            onRetry={() => void report.refetch()}
            completePartially={completePartially}
            onCompletePartiallyChange={setCompletePartially}
          />
        </TabsContent>
        <TabsContent value="run">
          <RunTab
            batch={b}
            onCancel={() =>
              void cancel
                .mutateAsync()
                .then(() => toast.success('Cancelled'))
                .catch((err) => toast.error(err instanceof Error ? err.message : 'Cancel failed'))
            }
            cancelPending={cancel.isPending}
          />
        </TabsContent>
      </Tabs>
    </Page>
  );
}
